import React, { useState, useEffect, useCallback } from 'react';
import { seaportBody } from '@carboncare/schemas';
import { preflight } from '../../services/api/schemaPreflight';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import apiClient from '../../services/api';
import { useCoordinatePaste } from '../../hooks/useCoordinatePaste';
import PageHeader from '../../components/common/PageHeader';
import {
  StatusSnackbars,
  CountChip,
  usePaginationState,
  useReferenceSearch,
  NoResultsRow,
  SearchBarPaper,
  CrudTablePagination,
  DeleteConfirmDialog,
  PortCodeSkeletonRow,
  EditDeleteActions,
  CoordinatesCaption,
  makeLatLonFormChangeHandler,
  CrudFormDialog,
  PortSharedFormFields,
  validateDecimal,
} from './_shared';

interface Seaport {
  un_locode: string;
  name: string;
  city?: string;
  country?: string;
  country_name?: string;
  lat: number;
  lon: number;
  region?: string;
  created_at?: string;
  updated_at?: string;
}

const SeaportsPage: React.FC = () => {
  const [seaports, setSeaports] = useState<Seaport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const { page, setPage, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePaginationState(20);
  const { search, matchMode, searchProps } = useReferenceSearch(setPage);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [selectedSeaport, setSelectedSeaport] = useState<Seaport | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [seaportToDelete, setSeaportToDelete] = useState<Seaport | null>(null);

  const [formData, setFormData] = useState<Partial<Seaport>>({
    un_locode: '',
    name: '',
    city: '',
    country: '',
    country_name: '',
    lat: undefined,
    lon: undefined,
    region: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handlePasteCoordinates = useCoordinatePaste(setFormData, setFormErrors);

  const handleFormChange = makeLatLonFormChangeHandler(setFormData, setFormErrors);

  const fetchSeaports = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.get('/data-management/seaports', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          matchMode,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });

      if (response.data.success) {
        setSeaports(response.data.data);
        setTotalCount(response.data.pagination.total);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch seaports');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, matchMode]);

  useEffect(() => {
    fetchSeaports();
  }, [fetchSeaports]);

  const handleOpenDialog = (mode: 'add' | 'edit', seaport?: Seaport) => {
    setDialogMode(mode);
    if (mode === 'edit' && seaport) {
      setSelectedSeaport(seaport);
      // only the editable schema fields; id/created_at/updated_at would be
      // rejected by the strict update schema on PUT
      setFormData({
        un_locode: seaport.un_locode,
        name: seaport.name,
        city: seaport.city || '',
        country: seaport.country || '',
        country_name: seaport.country_name || '',
        lat: seaport.lat,
        lon: seaport.lon,
        region: seaport.region || ''
      });
    } else {
      setSelectedSeaport(null);
      setFormData({
        un_locode: '',
        name: '',
        city: '',
        country: '',
        country_name: '',
        lat: undefined,
        lon: undefined,
        region: ''
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSeaport(null);
    setFormErrors({});
  };

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.un_locode) newErrors.un_locode = 'UN/LOCODE is required';
    if (!formData.name) newErrors.name = 'Name is required';
    const latError = validateDecimal(formData.lat, 'Latitude');
    const lonError = validateDecimal(formData.lon, 'Longitude');
    if (latError) newErrors.lat = latError;
    if (lonError) newErrors.lon = lonError;

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    try {
      if (dialogMode === 'add') {
        preflight(seaportBody, formData);
        const response = await apiClient.post('/data-management/seaports', formData);
        if (response.data.success) {
          setSuccess('Seaport added successfully');
          fetchSeaports();
        }
      } else {
        const response = await apiClient.put(
          `/data-management/seaports/${selectedSeaport?.un_locode}`,
          formData
        );
        if (response.data.success) {
          setSuccess('Seaport updated successfully');
          fetchSeaports();
        }
      }
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save seaport');
    }
  };

  const handleDeleteClick = (seaport: Seaport) => {
    setSeaportToDelete(seaport);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!seaportToDelete) return;

    try {
      const response = await apiClient.delete(
        `/data-management/seaports/${seaportToDelete.un_locode}`
      );
      if (response.data.success) {
        setSuccess('Seaport deleted successfully');
        fetchSeaports();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete seaport');
    } finally {
      setDeleteDialogOpen(false);
      setSeaportToDelete(null);
    }
  };

  return (
    <Box>
      <PageHeader
        description="View and maintain the seaport reference dataset."
        actions={
          <>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchSeaports}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => handleOpenDialog('add')}
            >
              Add Seaport
            </Button>
          </>
        }
      />

      <StatusSnackbars
        error={error}
        success={success}
        onClearError={() => setError('')}
        onClearSuccess={() => setSuccess('')}
      />

      <SearchBarPaper
        placeholder="Search by UN/LOCODE, name, city, or country..."
        {...searchProps}
        countSlot={<CountChip loading={loading} count={totalCount} label="seaports" />}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>UN/LOCODE</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Coordinates</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 10 }, (_, i) => `skeleton-${i}`).map((rowKey, idx) => (
                  <PortCodeSkeletonRow key={rowKey} idx={idx} codeWidth={62} />
                ))
              : seaports.length === 0
                ? <NoResultsRow colSpan={6} search={search} matchMode={matchMode} />
                : seaports.map((seaport) => (
                  <TableRow key={seaport.un_locode} hover>
                    <TableCell>
                      <Chip label={seaport.un_locode} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{seaport.name}</TableCell>
                    <TableCell>{seaport.city || '-'}</TableCell>
                    <TableCell>{seaport.country_name || seaport.country || '-'}</TableCell>
                    <TableCell>
                      <CoordinatesCaption lat={seaport.lat} lon={seaport.lon} />
                    </TableCell>
                    <EditDeleteActions
                      onEdit={() => handleOpenDialog('edit', seaport)}
                      onDelete={() => handleDeleteClick(seaport)}
                    />
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        <CrudTablePagination
          loading={loading}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <CrudFormDialog
        open={openDialog}
        title={dialogMode === 'add' ? 'Add New Seaport' : 'Edit Seaport'}
        onClose={handleCloseDialog}
        onSave={handleSave}
        saveLabel={dialogMode === 'add' ? 'Add' : 'Update'}
      >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="UN/LOCODE"
                value={formData.un_locode}
                onChange={handleFormChange('un_locode')}
                disabled={dialogMode === 'edit'}
                required
                inputProps={{ maxLength: 5 }}
                error={!!formErrors.un_locode}
                helperText={formErrors.un_locode}
              />
            </Grid>
            <PortSharedFormFields
              nameValue={formData.name}
              cityValue={formData.city}
              countryValue={formData.country}
              countryNameValue={formData.country_name}
              latValue={formData.lat}
              lonValue={formData.lon}
              regionValue={formData.region}
              nameLabel="Port Name"
              onNameChange={handleFormChange('name')}
              onCityChange={handleFormChange('city')}
              onCountryChange={handleFormChange('country')}
              onCountryNameChange={handleFormChange('country_name')}
              onLatChange={handleFormChange('lat')}
              onLonChange={handleFormChange('lon')}
              onRegionChange={handleFormChange('region')}
              onPaste={handlePasteCoordinates}
              nameError={formErrors.name}
              latError={formErrors.lat}
              lonError={formErrors.lon}
            />
          </Grid>
      </CrudFormDialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        Are you sure you want to delete seaport "{seaportToDelete?.name}" ({seaportToDelete?.un_locode})?
      </DeleteConfirmDialog>
    </Box>
  );
};

export default SeaportsPage;
